import { DocReviewKind, DocReviewReason, type ReviewQueueItem } from '../types/documentCapture.types';

/**
 * Review-overlay tab grouping (86d3rrfdk).
 *
 * ⚠️ Grouped by REASON, not the server's `Kind`. The server tags 4 of 6 NetSuite-side reasons as
 * `Kind=Scan` (`MultipleNsMatches`, `NsVoided`, `ClosedPeriod`, `LinkFailed`), so a kind-based tab
 * would hide the multi-match picker and voided items in the Scans tab. The FE owns the taxonomy.
 *
 * ⚠️ …EXCEPT where a reason is genuinely ambiguous, which `DecodeFailed` now is (86d3w2jrj FE-1).
 * Two different producers raise it: the replace gate parks an unreadable REPLACEMENT upload
 * (`Kind=Replacement`, has a document), and W3 quarantines an unreadable intake SCAN
 * (`Kind=Scan`, `documentId: null`). Routing both to Replacements sent the scan to a panel whose
 * only enabled control is Discard, which the server refuses on `Kind != Replacement` — the item was
 * visible and could not be resolved by anyone, which is worse than not showing it.
 *
 * So: reason decides the tab, and `Kind` breaks the tie where the reason cannot. Route items through
 * {@link tabForItem}, never {@link REASON_TO_TAB} directly.
 */

export type ReviewTab = 'scans' | 'netsuite' | 'replacements';

export const REVIEW_TAB_ORDER: ReviewTab[] = ['scans', 'netsuite', 'replacements'];

export const REVIEW_TAB_LABEL: Record<ReviewTab, string> = {
  scans: 'Scans',
  netsuite: 'NetSuite',
  replacements: 'Replacements',
};

/**
 * Rail-width labels. The landing page's Needs-review card gives each tab a third of ~330px and puts
 * the count beside the word, where "Replacements · 2" wraps; the overlay's tabs are full width and
 * use {@link REVIEW_TAB_LABEL}. Both spellings are the prototype's.
 */
export const REVIEW_TAB_SHORT_LABEL: Record<ReviewTab, string> = {
  scans: 'Scans',
  netsuite: 'NetSuite',
  replacements: 'Repl',
};

/**
 * One line under the rail card's tab row saying what the selected tab is asking of the reviewer
 * (86d3z1n04). It lives here beside {@link REVIEW_TAB_SHORT_LABEL} because it exists for the same
 * reason that map does — the rail is narrow. `Repl` is the abbreviation that narrowness bought, and
 * nobody outside the team expands it; this is where it gets spelled out.
 *
 * It also answers a question the tabs never did: before this, the only way to learn what
 * "NetSuite · 9" wanted from you was to click it and find out.
 */
export const REVIEW_TAB_HINT: Record<ReviewTab, string> = {
  scans: 'Scans waiting for a document number.',
  netsuite: 'Filed, but NetSuite hasn’t accepted the link yet.',
  replacements: 'A newer file arrived for a document that already has one.',
};

const REASON_TO_TAB: Record<DocReviewReason, ReviewTab> = {
  // Scans — the scan file itself needs a human to identify it.
  [DocReviewReason.NoBarcode]: 'scans',
  [DocReviewReason.UnknownPrefix]: 'scans',
  [DocReviewReason.NumberNotOnPage1]: 'scans',
  [DocReviewReason.MultipleNumbers]: 'scans',
  [DocReviewReason.NonConsecutiveRepeat]: 'scans',
  [DocReviewReason.NeverAppearedInNs]: 'scans',

  // NetSuite — the document is identified, but the NS link needs a human.
  [DocReviewReason.MultipleNsMatches]: 'netsuite',
  [DocReviewReason.NsVoided]: 'netsuite',
  [DocReviewReason.CustbodyConflict]: 'netsuite',
  [DocReviewReason.ClosedPeriod]: 'netsuite',
  [DocReviewReason.LinkFailed]: 'netsuite',
  [DocReviewReason.LinkRefused]: 'netsuite',
  // 14ykpqtwauu - DOP withheld the link itself; the question was about the NetSuite record. RETIRED
  // 14ykpqtx1z9: nothing raises it any more; the mapping stays so resolved items still file under a tab.
  [DocReviewReason.AppliedCredits]: 'netsuite',
  [DocReviewReason.LinkBroken]: 'netsuite',
  [DocReviewReason.WrongRecordFamily]: 'netsuite',

  // Replacements — a new scan supersedes a linked document, or a deliberate update was held
  // by the W9 match gate (86d3vbwup: mismatch / multiple / no barcode / unreadable).
  [DocReviewReason.SupersedePending]: 'replacements',
  [DocReviewReason.FewerPages]: 'replacements',
  [DocReviewReason.DecodeFailed]: 'replacements',
  [DocReviewReason.HeldMismatch]: 'replacements',
  [DocReviewReason.HeldMultiple]: 'replacements',
  [DocReviewReason.HeldNoBarcode]: 'replacements',

  // Scans — 86d3x48q7 (T13) AI second reader. All five are questions about WHICH DOCUMENT this
  // scan is, which is the Scans tab's job; none of them is a NetSuite-link problem. Mapped
  // explicitly rather than left to `tabForItem`'s `?? 'scans'` fallback: the fallback is a safety
  // net for a reason nobody mapped, and relying on it would mean a genuinely mis-tabbed reason
  // could never be told apart from one that simply landed there by default.
  [DocReviewReason.IdentificationConflict]: 'scans',
  [DocReviewReason.AiSuggestion]: 'scans',
  [DocReviewReason.Unconfirmed]: 'scans',
  [DocReviewReason.VerifierUnavailable]: 'scans',
  // Already LINKED — the spot check asks a person to confirm an auto-link nobody looked at. It is
  // still a "is this the right document?" question, so it belongs beside the others.
  [DocReviewReason.AutoLinkAudit]: 'scans',
};

/**
 * Which tab a review item belongs in.
 *
 * Reason first; `Kind` only disambiguates `DecodeFailed`, whose two producers need different panels
 * and different actions. A `Kind=Scan` DecodeFailed is an unreadable intake scan — it belongs in
 * Scans, where a reviewer can read the number off the paper and assign it by hand, or reject it as
 * junk. Both of those already work for an item with no document.
 *
 * Takes the whole item rather than `(reason, kind)` so a future ambiguity has somewhere to look
 * without touching all five call sites again.
 */
export function tabForItem(item: Pick<ReviewQueueItem, 'reason' | 'kind'>): ReviewTab {
  if (item.reason === DocReviewReason.DecodeFailed && item.kind === DocReviewKind.Scan) return 'scans';
  return REASON_TO_TAB[item.reason] ?? 'scans';
}

// ---------------------------------------------------------------- live-stream row → review item
//
// 86d3z2nz4. A "Needs review" row in the live stream now opens the review queue AT ITS ITEM, and the
// item is resolved from the queue ALREADY IN MEMORY — never by opening the overlay and letting it
// look the item up, which would put a network round trip between the click and the first visible
// change (CLAUDE.md responsiveness gate).
//
// These are pure and live here beside {@link tabForItem} because the answer is half a tab decision:
// {@link openReview} takes `(tab, id)` and the overlay filters to the active tab before selecting, so
// a WRONG TAB silently opens a different item or an empty panel (86d3w2jrj FE-1 — this exact bug has
// shipped once already). Tab and item have to be decided together, from the same item.
//
// The row shape is spelled structurally rather than imported as `JourneyRow`, so this module stays
// free of the stream reducers (and of everything they drag in).

/** The two fields of a stream row that can identify a review item. */
export interface ReviewRowKey {
  fileId: number | null;
  docNumber: string | null;
}

export interface ReviewRowTarget {
  /** The tab to open. Always the tab of {@link item}. */
  tab: ReviewTab;
  /**
   * The item to select. Always set: a target that names no item is worse than useless, because the
   * overlay's own fallback then picks the oldest item in the tab — see {@link resolveReviewRowTarget}.
   */
  reviewItemId: number;
  /** The item the tab and selection were derived from. */
  item: ReviewQueueItem;
}

/**
 * Lookup built once per render of the feed rather than scanned per row: the queue is capped at 100
 * items and the feed at 50 rows, and an O(n·m) scan on every keystroke-free re-render of a live list
 * is exactly the kind of waste the re-render gate is about.
 */
export interface ReviewTargetIndex {
  byFileId: Map<number, ReviewQueueItem>;
  byDocNumber: Map<string, ReviewQueueItem[]>;
}

export function buildReviewTargetIndex(items: readonly ReviewQueueItem[]): ReviewTargetIndex {
  const byFileId = new Map<number, ReviewQueueItem>();
  const byDocNumber = new Map<string, ReviewQueueItem[]>();
  for (const item of items) {
    // First wins: the queue arrives oldest-first from the server, and for a single scan file there is
    // at most one open item anyway.
    if (item.fileId != null && !byFileId.has(item.fileId)) byFileId.set(item.fileId, item);
    if (item.docNumber != null) {
      const bucket = byDocNumber.get(item.docNumber);
      if (bucket) bucket.push(item);
      else byDocNumber.set(item.docNumber, [item]);
    }
  }
  return { byFileId, byDocNumber };
}

/**
 * Which review item (if any) a `review` stream row should open.
 *
 * Returns null when NOTHING in the open queue matches — which is the steady state, not an edge case:
 * `RejectScanAsync` and `KeepWatchingAsync` publish no stream event, deliberately, so a row sits at
 * `review` indefinitely after its item has been resolved, on every open page including the actor's
 * own. Resolving at render time and hiding the affordance when this returns null is what stops a
 * stale click from opening somebody else's paper.
 *
 * **Tie-break** (a document can hold `SupersedePending` AND `CustbodyConflict` at once):
 *   1. A file-id match wins outright — it names one scan, so there is nothing to guess.
 *   2. Otherwise a document-number match. Exactly one → open it.
 *   3. More than one → open the FIRST match, and let the queue column beside it show the rest.
 *
 * ⚠️ Step 3 must name an item. Returning null here looks like the humbler choice — "let the reviewer
 * pick" — but the overlay has no no-selection state to fall back to: `ReviewOverlay` resolves
 * `tabItems.find(i => i.id === selectedId) ?? tabItems[0]`, and `tabItems[0]` is the oldest item in
 * that whole tab, which is very likely a DIFFERENT document. So declining to choose does not hand
 * the choice to the reviewer; it silently hands it to a fallback that is wrong more often than the
 * first match would be. That is exactly the failure 86d3w2jrj FE-1 fixed once already (a wrong tab
 * quietly opening a different item), one level down.
 */
export function resolveReviewRowTarget(
  row: ReviewRowKey,
  index: ReviewTargetIndex
): ReviewRowTarget | null {
  if (row.fileId != null) {
    const byFile = index.byFileId.get(row.fileId);
    if (byFile) return { tab: tabForItem(byFile), reviewItemId: byFile.id, item: byFile };
  }

  if (row.docNumber != null) {
    const matches = index.byDocNumber.get(row.docNumber);
    if (matches && matches.length > 0) {
      const first = matches[0];
      return { tab: tabForItem(first), reviewItemId: first.id, item: first };
    }
  }

  return null;
}
